import { createContext, useContext } from "react";

/**
 * How the transcript follows the bottom while content grows.
 *
 * - `instant` — pin `scrollTop` synchronously in ResizeObserver.
 * - `spring` — ease toward the moving bottom with a rAF spring (production).
 */
export type ScrollFollowMode = "instant" | "spring";

/**
 * Spring integration used while `mode` is `spring`.
 *
 * - `velocity` — second-order damped oscillator that carries velocity across
 *   retargets (continuous under fast streaming growth).
 * - `lerp` — memoryless exponential ease of remaining distance.
 */
export type SpringMotion = "velocity" | "lerp";

/**
 * Fraction of remaining distance closed per {@link SPRING_LERP_FRAME_MS}
 * (calibrated for 60Hz). Actual steps scale by elapsed time so 120Hz and 60Hz
 * catch up in the same wall-clock duration.
 */
export const DEFAULT_SPRING_LERP = 0.08;
/** Nominal frame duration the lerp knob is calibrated against (60Hz). */
export const SPRING_LERP_FRAME_MS = 1000 / 60;
/** Cap a single integration step so a backgrounded tab does not jump. */
export const SPRING_MAX_STEP_MS = 64;
/** Minimum progress per calibrated frame, preventing stalls after pixel rounding. */
export const SPRING_MIN_STEP_PX = 0.5;

/**
 * Perceptual catch-up time for the velocity spring (~1/ω). Wall-clock settling
 * is a few multiples of this under critical damping.
 */
export const DEFAULT_SPRING_RESPONSE_MS = 180;
/** Critical damping — settles at the bottom without oscillating past it. */
export const DEFAULT_SPRING_DAMPING_RATIO = 1;
/** Default spring integration while following. */
export const DEFAULT_SPRING_MOTION: SpringMotion = "velocity";
/**
 * Velocity below this (px/ms) counts as settled when position is also within
 * the settle epsilon — stops the rAF loop from creeping forever.
 */
export const SPRING_VELOCITY_SETTLE_PX_PER_MS = 0.001;

/**
 * Slack (in px) within which a scroll container counts as "at the bottom"
 * for auto-following new content: the transcript (`useScrollManager`) and its
 * nested follow windows (`useFollowBottomScroll`) re-pin only when the user
 * is genuinely parked there.
 */
export const BOTTOM_THRESHOLD_PX = 70;
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Time-scaled exponential ease: `lerp` is the fraction closed per
 * {@link SPRING_LERP_FRAME_MS}; returns the fraction to close over `dtMs`.
 */
export function springStepAlpha(lerp: number, dtMs: number): number {
  if (lerp <= 0) return 0;
  if (lerp >= 1) return 1;
  const dt = Math.min(Math.max(dtMs, 0), SPRING_MAX_STEP_MS);
  if (dt === 0) return 0;
  return 1 - Math.pow(1 - lerp, dt / SPRING_LERP_FRAME_MS);
}

/** Distance to move this frame, with a refresh-rate-independent minimum. */
export function springStepDistance(deltaPx: number, lerp: number, dtMs: number): number {
  if (deltaPx === 0) return 0;
  const dt = Math.min(Math.max(dtMs, 0), SPRING_MAX_STEP_MS);
  const easedDistance = Math.abs(deltaPx) * springStepAlpha(lerp, dt);
  const minimumDistance = SPRING_MIN_STEP_PX * (dt / SPRING_LERP_FRAME_MS);
  return Math.sign(deltaPx) * Math.min(Math.abs(deltaPx), Math.max(easedDistance, minimumDistance));
}
export type VelocitySpringStep = {
  stepPx: number;
  velocityPxPerMs: number;
};

/**
 * Closed-form damped-oscillator step toward a fixed target for this frame.
 *
 * `deltaPx` is `target - current` (positive when scrolling down). Velocity is
 * `d(scrollTop)/dt` in px/ms and is preserved across retargets by the caller —
 * only this integration updates it. Uses the analytic critically / overdamped
 * solution so two half-frames match one full frame (refresh-rate independent).
 * `dampingRatio` is clamped to >= 1 so the step never oscillates past the
 * target.
 */
export function velocitySpringStep(deltaPx: number, velocityPxPerMs: number, responseMs: number, dampingRatio: number, dtMs: number): VelocitySpringStep {
  const dt = Math.min(Math.max(dtMs, 0), SPRING_MAX_STEP_MS);
  if (deltaPx === 0 && Math.abs(velocityPxPerMs) < SPRING_VELOCITY_SETTLE_PX_PER_MS) {
    return {
      stepPx: 0,
      velocityPxPerMs: 0
    };
  }
  if (dt === 0) {
    return {
      stepPx: 0,
      velocityPxPerMs: velocityPxPerMs
    };
  }
  const response = Math.max(responseMs, 1);
  const omega = 1 / response;
  const zeta = Math.max(dampingRatio, 1);

  // Error coordinate: x = current - target = -delta. Want x → 0.
  const x0 = -deltaPx;
  const v0 = velocityPxPerMs;
  let x1: number;
  let v1: number;
  if (zeta === 1 || Math.abs(zeta - 1) < 1e-9) {
    // Critically damped: x(t) = (x0 + (v0 + ω x0) t) e^(-ω t)
    const exp = Math.exp(-omega * dt);
    const b = v0 + omega * x0;
    x1 = (x0 + b * dt) * exp;
    v1 = (v0 - omega * b * dt) * exp;
  } else {
    // Overdamped: x(t) = C1 e^(r1 t) + C2 e^(r2 t)
    const alpha = zeta * omega;
    const beta = omega * Math.sqrt(zeta * zeta - 1);
    const r1 = -alpha + beta;
    const r2 = -alpha - beta;
    const denom = r1 - r2;
    const c1 = (v0 - r2 * x0) / denom;
    const c2 = x0 - c1;
    const e1 = Math.exp(r1 * dt);
    const e2 = Math.exp(r2 * dt);
    x1 = c1 * e1 + c2 * e2;
    v1 = c1 * r1 * e1 + c2 * r2 * e2;
  }
  let stepPx = x1 - x0;

  // Floor creeping progress so sub-pixel rounding cannot stall follow, without
  // allowing the step to pass the remaining distance to the target.
  const minimumDistance = SPRING_MIN_STEP_PX * (dt / SPRING_LERP_FRAME_MS);
  if (deltaPx !== 0 && Math.abs(stepPx) < minimumDistance) {
    const towardTarget = Math.sign(deltaPx) * minimumDistance;
    stepPx = Math.sign(deltaPx) * Math.min(Math.abs(deltaPx), Math.abs(towardTarget));
    // Keep velocity consistent with a minimum crawl toward the target.
    if (Math.sign(v1) !== Math.sign(deltaPx) || Math.abs(v1) < SPRING_VELOCITY_SETTLE_PX_PER_MS) {
      v1 = stepPx / dt;
    }
  }

  // Hard clamp: never move past the target in one step when critically /
  // overdamped (analytic drift or the min-step floor could otherwise).
  if (deltaPx !== 0 && Math.sign(stepPx) === Math.sign(deltaPx)) {
    if (Math.abs(stepPx) >= Math.abs(deltaPx)) {
      return {
        stepPx: deltaPx,
        velocityPxPerMs: 0
      };
    }
  } else if (deltaPx !== 0 && Math.sign(stepPx) !== 0 && Math.sign(stepPx) !== Math.sign(deltaPx)) {
    // Should not happen for ζ >= 1 from rest; kill reverse motion.
    return {
      stepPx: 0,
      velocityPxPerMs: 0
    };
  }
  return {
    stepPx,
    velocityPxPerMs: v1
  };
}

/** Snap instead of easing when farther than this from the bottom. */
export const DEFAULT_SPRING_SNAP_DISTANCE_PX = 480;
export type ScrollFollowConfig = {
  mode: ScrollFollowMode;
  /** Spring integration while `mode` is `spring`. Defaults to velocity. */
  springMotion?: SpringMotion;
  /**
   * Fraction of remaining distance closed per 60Hz frame (0–1). Used when
   * `springMotion` is `lerp`. Steps are scaled by elapsed time so the catch-up
   * rate is refresh-rate independent.
   */
  lerp?: number;
  /**
   * Perceptual catch-up time in ms (~1/ω) for the velocity spring. Used when
   * `springMotion` is `velocity`.
   */
  responseMs?: number;
  /**
   * Damping ratio for the velocity spring (>= 1 = no overshoot). Used when
   * `springMotion` is `velocity`.
   */
  dampingRatio?: number;
  /** Override for spring snap distance in px. */
  snapDistancePx?: number;
};
export const ScrollFollowContext = createContext<ScrollFollowConfig>({
  mode: "spring",
  springMotion: DEFAULT_SPRING_MOTION,
  lerp: DEFAULT_SPRING_LERP,
  responseMs: DEFAULT_SPRING_RESPONSE_MS,
  dampingRatio: DEFAULT_SPRING_DAMPING_RATIO
});
export function useScrollFollowConfig() {
  return useContext(ScrollFollowContext);
}
export function useScrollFollowMode() {
  return useScrollFollowConfig().mode;
}